/*
 * Mobile bottom navigation - controller
 * =============================================================================
 * Drives the theme's existing components. It never builds a drawer of its own.
 *
 *   menu   -> Alpine.store('xMobileNav')                assets/header.js
 *   filter -> button#btn-filter -> #FacetsFilterDrawer   native facets drawer
 *   cart   -> document event 'cart:minicart:open'        assets/cart-drawer.js
 *   count  -> mirrored from #cart-icon-bubble .cart-bubble__container[data-count-item]
 *
 * Actions are dispatched by delegation on [data-mbn-action], so reordering,
 * adding or removing blocks in the Theme Editor needs no re-binding.
 *
 * MENU POSITIONING
 * The drawer panel is position:absolute inside #x-header-container, which is static
 * and scrolls away, so opening from mid-page put the drawer far outside the viewport.
 * While the drawer is open we add .mbn-menu-open to <html>; the section CSS then pins
 * the header to the viewport and pads the body by its exact height so the scroll
 * position cannot clamp. Everything inside the drawer - close button, focus trap,
 * scroll lock - keeps working natively.
 */
(function () {
  'use strict';

  var HEADER_BADGE = '#cart-icon-bubble .cart-bubble__container';
  var CART_UPDATED = 'maximize:cart:updated';    // CART_EVENT.cartUpdate, assets/constants.js
  var CART_OPEN = 'cart:minicart:open';          // listened for on document by <cart-drawer>
  var MENU_DRAWER = '#x-header-mobile';
  var HEADER_WRAP = '#x-header-container';
  var FILTER_TRIGGER = '#btn-filter';
  var FILTER_DRAWER = '#FacetsFilterDrawer';
  var OPEN_CLASS = 'mbn-menu-open';

  var instances = new Map();   // root element -> { ac, mo }
  var globalBound = false;
  var lastMenuTrigger = null;
  var lastFilterTrigger = null;

  /* ------------------------------------------------- occupied height ---- */

  var OCCUPIED_VAR = '--mobile-bottom-nav-occupied-height';

  /* How much space the bar really takes at the bottom of the viewport.
     Measuring the distance from the bar's bottom edge to the viewport bottom
     captures the configured offset AND the safe-area inset in one number, so
     neither can be double counted. The section CSS ships a settings-derived
     fallback; this overrides it with the truth once rendered. */
  function syncOccupiedHeight(root) {
    var el = root || document.querySelector('[data-mobile-bottom-nav]');
    if (!el) { document.documentElement.style.removeProperty(OCCUPIED_VAR); return; }
    var cs = getComputedStyle(el);
    // hidden by the breakpoint or by page rules - let the stylesheet decide
    if (cs.display === 'none' || cs.visibility === 'hidden') {
      document.documentElement.style.removeProperty(OCCUPIED_VAR);
      return;
    }
    var r = el.getBoundingClientRect();
    if (!r.height) { document.documentElement.style.removeProperty(OCCUPIED_VAR); return; }
    var occupied = Math.round(r.height + Math.max(0, window.innerHeight - r.bottom));
    document.documentElement.style.setProperty(OCCUPIED_VAR, occupied + 'px');
  }

  /* ---------------------------------------------------------------- cart */

  function readHeaderCount() {
    var wrap = document.querySelector('#cart-icon-bubble');
    // No header bubble wrapper at all: the header has not rendered, so the count is
    // genuinely unknown and the badge must be left alone.
    if (!wrap) return null;
    var el = wrap.querySelector('.cart-bubble__container');
    // Wrapper present but no bubble inside: the theme drops it when the cart empties,
    // so this is a real zero rather than missing information.
    if (!el) return 0;
    var raw = el.getAttribute('data-count-item');
    if (raw === null || raw === '') raw = (el.textContent || '').replace(/\D/g, '');
    if (raw === '') return 0;
    var n = parseInt(raw, 10);
    return isNaN(n) ? 0 : n;
  }

  function paintCount(root, count) {
    if (count === null) return;
    var link = root.querySelector('[data-mbn-action="cart"]');
    if (!link) return;
    var badge = link.querySelector('[data-mbn-badge]');
    var showZero = link.getAttribute('data-show-zero') === 'true';
    if (badge) {
      badge.textContent = count > 99 ? '99+' : String(count);
      badge.hidden = count <= 0 && !showZero;
    }
    // accessible name always carries the real count, even when the badge shows 99+
    link.setAttribute('aria-label', count === 1 ? '1 item in cart' : count + ' items in cart');
  }

  function openCart() {
    document.dispatchEvent(new CustomEvent(CART_OPEN));
  }

  /* ---------------------------------------------------------------- menu */

  function menuStore() {
    try { return window.Alpine && window.Alpine.store ? window.Alpine.store('xMobileNav') : null; }
    catch (e) { return null; }
  }

  function pinHeader() {
    var h = document.querySelector(HEADER_WRAP);
    if (!h) return;
    // measure BEFORE pinning, while it is still in normal flow
    var height = Math.round(h.getBoundingClientRect().height) || 0;
    document.documentElement.style.setProperty('--mbn-header-h', height + 'px');
    document.documentElement.classList.add(OPEN_CLASS);
  }

  function unpinHeader() {
    document.documentElement.classList.remove(OPEN_CLASS);
    document.documentElement.style.removeProperty('--mbn-header-h');
  }

  function openMenu(trigger) {
    var store = menuStore();
    lastMenuTrigger = trigger || null;
    pinHeader();                          // pin first so the drawer lands in the viewport
    if (store) {
      if (!store.show) store.toggle();    // toggle() is the store's only opener
    } else {
      // Alpine has not booted - use the theme's own trigger. Same drawer either way.
      var native = document.querySelector('.header-mobile-toggle-icon');
      if (native) native.click(); else { unpinHeader(); return false; }
    }
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    return true;
  }

  function closeMenu() {
    var store = menuStore();
    if (store && store.close) store.close();
    else {
      var native = document.querySelector('.header-mobile-toggle-icon');
      if (native) native.click();
    }
  }

  /* Watch the drawer's own open/closed state rather than assuming ours is the only
     thing that changes it - the header hamburger and in-drawer links do too. */
  function watchMenuState() {
    var drawer = document.querySelector(MENU_DRAWER);
    if (!drawer || !('MutationObserver' in window)) return null;
    var mo = new MutationObserver(function () {
      var open = drawer.classList.contains('active');
      if (open) {
        if (!document.documentElement.classList.contains(OPEN_CLASS)) pinHeader();
      } else {
        unpinHeader();
        document.querySelectorAll('[data-mbn-action="menu"]').forEach(function (b) {
          b.setAttribute('aria-expanded', 'false');
        });
        if (lastMenuTrigger && document.contains(lastMenuTrigger)) {
          lastMenuTrigger.focus({ preventScroll: true });   // focus back, no scroll jump
          lastMenuTrigger = null;
        }
      }
    });
    mo.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    return mo;
  }

  /* -------------------------------------------------------------- filter */

  function openFilter(trigger) {
    var btn = document.querySelector(FILTER_TRIGGER);
    if (!btn) return false;
    lastFilterTrigger = trigger || null;
    // The native drawer is already position:fixed, so clicking its own trigger works
    // from any scroll position even while the trigger itself is off screen.
    btn.click();
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    return true;
  }

  /* The facets form is re-rendered by the theme's AJAX filtering, which does not
     re-render this section - so the server-rendered count would go stale. Recount
     from the form itself, which is the same source the drawer displays. */
  function syncFilterCount() {
    var form = document.querySelector('#FacetFiltersForm');
    document.querySelectorAll('[data-mbn-filter-badge]').forEach(function (badge) {
      if (!form) { badge.hidden = true; return; }
      var n = form.querySelectorAll('input[type="checkbox"]:checked').length;
      badge.textContent = n > 99 ? '99+' : String(n);
      badge.hidden = n === 0;
    });
    document.querySelectorAll('[data-mbn-action="filter"]').forEach(function (b) {
      var n = form ? form.querySelectorAll('input[type="checkbox"]:checked').length : 0;
      var base = b.getAttribute('data-aria-base') || b.getAttribute('aria-label') || 'Open filters';
      if (!b.getAttribute('data-aria-base')) b.setAttribute('data-aria-base', base);
      b.setAttribute('aria-label', n > 0 ? base + ' (' + n + ' active)' : base);
    });
  }

  function watchFilterState() {
    var d = document.querySelector(FILTER_DRAWER);
    if (!d || !('MutationObserver' in window)) return null;
    var mo = new MutationObserver(function () {
      syncFilterCount();
      var open = getComputedStyle(d).display !== 'none';
      if (!open) {
        document.querySelectorAll('[data-mbn-action="filter"]').forEach(function (b) {
          b.setAttribute('aria-expanded', 'false');
        });
        if (lastFilterTrigger && document.contains(lastFilterTrigger)) {
          lastFilterTrigger.focus({ preventScroll: true });
          lastFilterTrigger = null;
        }
      }
    });
    mo.observe(d, { attributes: true, attributeFilter: ['style', 'class'] });
    return mo;
  }

  /* ----------------------------------------------------------------- init */

  function init(root) {
    if (!root || instances.has(root)) return;

    var ac = new AbortController();
    var signal = ac.signal;

    // Single delegated handler - survives block reordering with no re-binding.
    root.addEventListener('click', function (e) {
      var el = e.target.closest ? e.target.closest('[data-mbn-action]') : null;
      if (!el || !root.contains(el)) return;
      var action = el.getAttribute('data-mbn-action');

      if (action === 'menu') { e.preventDefault(); openMenu(el); return; }
      if (action === 'filter') { e.preventDefault(); openFilter(el); return; }
      if (action === 'cart') {
        // leave modifier-clicks and the no-JS href alone
        if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        openCart();
      }
    }, { signal: signal });

    var sync = function () { paintCount(root, readHeaderCount()); };
    document.addEventListener(CART_UPDATED, sync, { signal: signal });

    // Narrowly scoped fallback: some add-to-cart paths re-render the header section
    // without dispatching the event. Watching one element cannot loop, because we
    // never write to the element we observe.
    var mos = [];
    // Observe the bubble's WRAPPER, not the bubble itself. The theme replaces the inner
    // bubble when the cart empties, which would silently kill an observer bound to it.
    var wrap = document.querySelector('#cart-icon-bubble');
    if (wrap && 'MutationObserver' in window) {
      var mo = new MutationObserver(sync);
      mo.observe(wrap, { attributes: true, attributeFilter: ['data-count-item'], childList: true, characterData: true, subtree: true });
      mos.push(mo);
    }
    // recount active filters whenever the facets form is swapped by AJAX filtering
    var ffWrap = document.querySelector('#FacetsWrapperBar') || document.querySelector('#FacetFiltersForm');
    if (ffWrap && 'MutationObserver' in window) {
      var mf = new MutationObserver(syncFilterCount);
      mf.observe(ffWrap, { childList: true, subtree: true });
      mos.push(mf);
    }
    document.addEventListener('shopify:section:load', syncFilterCount, { signal: signal });

    // The drawer's own facet inputs live outside #FacetsWrapperBar, so a childList
    // observer there never sees them change. Listen for the change itself, then again
    // once the theme's AJAX re-render has settled.
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      if (!t.closest('#FacetFiltersForm, #FacetsFilterDrawer')) return;
      syncFilterCount();
      setTimeout(syncFilterCount, 1500);
    }, { signal: signal });

    // Keep the occupied-height variable true as the bar changes shape: bar height,
    // label visibility, block add/remove/reorder and editor edits all resize it.
    var measure = function () { syncOccupiedHeight(root); };
    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(measure);
      ro.observe(root);
      mos.push({ disconnect: function () { ro.disconnect(); } });
    }
    window.addEventListener('resize', measure, { signal: signal });          // viewport / safe-area
    window.addEventListener('orientationchange', measure, { signal: signal });
    measure();

    var m1 = watchMenuState(); if (m1) mos.push(m1);
    var m2 = watchFilterState(); if (m2) mos.push(m2);

    // NOTE: the filter block's visibility is decided in Liquid, where collection.filters
    // is known for certain. Nothing is removed here - #btn-filter belongs to the collection
    // section, which can render after this controller initialises, and removing the button
    // on that race permanently deleted a working control.

    instances.set(root, { ac: ac, mos: mos });
    sync();
    syncFilterCount();
  }

  function teardown(root) {
    var inst = instances.get(root);
    if (!inst) return;
    inst.ac.abort();
    inst.mos.forEach(function (m) { m.disconnect(); });
    instances.delete(root);
    // the bar is going away - drop the variable so the Back to top button falls
    // back to whatever the theme's own stylesheet says
    if (!document.querySelector('[data-mobile-bottom-nav]')) {
      document.documentElement.style.removeProperty(OCCUPIED_VAR);
    }
  }

  function boot(scope) {
    (scope || document).querySelectorAll('[data-mobile-bottom-nav]').forEach(init);
  }

  /* Global listeners are bound exactly once per page, no matter how many times a
     section reload re-runs this file. */
  function bindGlobal() {
    if (globalBound) return;
    globalBound = true;

    // Escape closes the menu. The theme's own handler only fires while focus is
    // inside the header, which is not the case when opened from the bottom bar.
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var d = document.querySelector(MENU_DRAWER);
      if (d && d.classList.contains('active')) closeMenu();
    });

    document.addEventListener('shopify:section:load', function (e) {
      if (!e.target || !e.target.querySelectorAll) return;
      boot(e.target);
      // The header can be re-rendered independently, which detaches the bubble observer.
      // Re-initialise the bar's instances so they observe the new element.
      if (e.target.querySelector('#cart-icon-bubble')) {
        document.querySelectorAll('[data-mobile-bottom-nav]').forEach(function (root) {
          teardown(root); init(root);
        });
      }
    });
    document.addEventListener('shopify:section:unload', function (e) {
      if (!e.target || !e.target.querySelectorAll) return;
      e.target.querySelectorAll('[data-mobile-bottom-nav]').forEach(teardown);
    });
    // Selecting a block in the editor should preview its action.
    document.addEventListener('shopify:block:select', function (e) {
      var el = e.target && e.target.querySelector ? e.target.querySelector('[data-mbn-action]') : null;
      if (!el) return;
      var a = el.getAttribute('data-mbn-action');
      if (a === 'menu') openMenu(el);
      if (a === 'filter') openFilter(el);
    });
    document.addEventListener('shopify:block:deselect', function () {
      var d = document.querySelector(MENU_DRAWER);
      if (d && d.classList.contains('active')) closeMenu();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bindGlobal(); boot(); });
  } else { bindGlobal(); boot(); }
})();
